import React, { useCallback, useRef, useState } from 'react';
import { Button, Row } from 'antd';
import dayjs from 'dayjs';
import { pickingItemBySKU } from 'services/smart';
import { playAudio } from 'utils';
import FinishDialog from './FinishDialog';
import FailDialog from './FailDialog';
import { message } from 'components/common';
import { fetchPickingBySKU } from 'services/smart';
import {
  SmartSpace,
  SmartFormLabel,
  SmartLabel,
  SmartRow,
  SmartText,
  SmartScanner,
  SmartInputNumber,
  SmartBlock,
  SmartButton,
  GreenColor,
  YellowColor,
  SmartBackButton,
  SmartPickAll,
  SmartSpin,
} from '../../../SmartComponent';
import { useTranslation } from 'react-i18next';

type ScanQtyProps = {
  product: SmartPickSKU;
  location: WarehouseLocationRow;
  onSubmit: (product: SmartPickSKU) => void;
  onBack: (
    step: 'scanSKU' | 'scanLocaiton' | 'scanQty',
    nextLocation?: string
  ) => void;
};
export default function ScanQty(props: ScanQtyProps): JSX.Element {
  const { product, location, onBack, onSubmit } = props;
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [scannedQty, setScannedQty] = useState(0);
  const [scannedAt, setScannedAt] = useState<Date | undefined>();
  const [scannedStatus, setScannedStatus] = useState<
    'success' | 'fail' | 'default'
  >('default');
  const [finishStatus, setFinishStatus] = useState<'default' | 'finished'>(
    'default'
  );
  const { pickQty } = product;
  const failSKURef = useRef('');
  /**
   * Post qty
   */
  const handleSubmit = useCallback(
    async (source: 'skip' | 'auto', qty: number) => {
      if (qty > 0) {
        setSubmitting(true);
        try {
          const res = await pickingItemBySKU({
            PickListNum: product.pickListNum,
            LocationCode: location.locationCode || '',
            SKU: product.sku || product.upc,
            PickedQty: qty,
          });
          if (res.isSuccess) {
            setSubmitting(false);
            if (source === 'skip') {
              onBack('scanLocaiton');
            } else {
              setFinishStatus('finished');
            }
          }
        } catch (error) {
          setSubmitting(false);
        }
      }
    },
    [product, location, onBack]
  );
  /**
   * hanle finish status
   */
  const handleFinishStatus = useCallback(
    async (nextScannedQty: number) => {
      //Whether the SKUS has been scanned
      if (pickQty > 0 && nextScannedQty === pickQty) {
        handleSubmit('auto', nextScannedQty);
      }
    },
    [pickQty, handleSubmit]
  );
  /**
   * handle scan sku  event
   */
  const handleScannerChange = useCallback(
    async (nextValue: string) => {
      if (
        nextValue === product.sku ||
        (nextValue === product.upc && product.upc)
      ) {
        playAudio('success');
        setScannedStatus('success');
        const nextQty = scannedQty + 1;
        setScannedQty(nextQty);
        handleFinishStatus(nextQty);
      } else {
        playAudio('error');
        failSKURef.current = nextValue;
        setScannedStatus('fail');
      }
      setScannedAt(new Date());
    },
    [scannedQty, failSKURef, product, handleFinishStatus]
  );
  /**
   * handle finish event
   */
  const handleFinish = useCallback(
    async (nextValue) => {
      if (nextValue) {
        try {
          const res = await fetchPickingBySKU(nextValue);
          if (res.isSuccess) {
            if (res.data.pickQty <= 0) {
              playAudio('error');
              message.warning(`'${nextValue}' had done!`);
            } else {
              playAudio('success');
              onSubmit(res.data);
            }
          } else {
            playAudio('error');
            message.warning(`'${nextValue}' not found!`);
          }
        } catch (error) {
          onBack('scanSKU');
        }
      } else {
        onBack('scanSKU');
      }
    },
    [onBack, onSubmit]
  );
  const scannedAtStr = dayjs(scannedAt).format('hh:mm:ss A');
  const successTip = t("smart.pick.successTip", { value: scannedAtStr });
  const failTip = t('smart.pick.failedTip', { value: scannedAtStr });
  const buttonTitle = t("smart.pick.skipLocation");
  return (
    <>
      {scannedStatus === 'fail' && product && (
        <FailDialog
          failSKU={failSKURef.current}
          sku={product.sku || ''}
          upc={product.upc || ''}
          onSubmit={(ok) => {
            if (ok) {
              handleScannerChange(product.sku || '');
            } else {
              setScannedStatus('default');
            }
            setTimeout(() => {
              document.getElementById('scanner')?.focus();
            });
          }}
        />
      )}
      {finishStatus === 'finished' && (
        <FinishDialog product={product} onSubmit={handleFinish} />
      )}
      <SmartSpin loading={submitting} />
      <SmartSpace>
        <SmartRow style={{ marginTop: 10 }}>
          <SmartLabel>
            <SmartFormLabel>
              {t("smart.pick.pickSku")}
            </SmartFormLabel>
          </SmartLabel>
          <SmartText>{product.sku}</SmartText>
        </SmartRow>
        <SmartRow style={{ marginTop: 10 }}>
          <SmartLabel>
            <SmartFormLabel>
              {t("smart.pick.pickUpc")}
            </SmartFormLabel>
          </SmartLabel>
          <SmartText>{product.upc}</SmartText>
        </SmartRow>
        <SmartRow style={{ marginTop: 10, justifyContent: 'space-between' }}>
          <SmartRow>
            <SmartLabel>
              <SmartFormLabel>
                {t("common.location")}
              </SmartFormLabel>
            </SmartLabel>
            <SmartText>{location?.locationCode}</SmartText>
          </SmartRow>
          {scannedQty === 0 && (
            <Button
              size="small"
              onClick={() => {
                onBack('scanLocaiton');
              }}
            >
              {t("smart.pick.changeLocation")}
            </Button>
          )}
        </SmartRow>
        <SmartRow style={{ marginTop: 10 }}>
          <SmartLabel>
            <SmartFormLabel>
              {t("common.title")}
            </SmartFormLabel>
          </SmartLabel>
          <SmartText>{product ? product.itemTitle : ''}</SmartText>
        </SmartRow>
        <SmartRow style={{ marginTop: 10 }}>
          <SmartLabel>
            <SmartFormLabel>
              {t("smart.pick.pickQty")}
            </SmartFormLabel>
          </SmartLabel>
          <SmartInputNumber
            value={pickQty}
            style={{ width: 95, backgroundColor: 'rgb(255,253,128)' }}
          />
        </SmartRow>
        <SmartRow style={{ marginTop: 10 }}>
          <SmartLabel>
            <SmartFormLabel>
              {t("smart.pick.scan")}
            </SmartFormLabel>
          </SmartLabel>
          <SmartScanner
            id="scanner"
            style={{ width: 190 }}
            onChangeValue={handleScannerChange}
          />
          {scannedStatus === 'success' && (
            <SmartRow style={{ color: GreenColor }}>{successTip}</SmartRow>
          )}
          {scannedStatus === 'fail' && (
            <SmartRow style={{ color: YellowColor }}>{failTip}</SmartRow>
          )}
        </SmartRow>
        <SmartRow style={{ marginTop: 10, justifyContent: 'space-between' }}>
          <Row>
            <SmartLabel>
              <SmartFormLabel>
                {t("smart.pick.scannedQty")}
              </SmartFormLabel>
            </SmartLabel>
            <SmartInputNumber
              value={scannedQty}
              readOnly
              style={{ width: 95, backgroundColor: 'rgb(208,248,157)' }}
            />
          </Row>
          <SmartPickAll
            style={{ marginLeft: 20 }}
            onClick={() => {
              setScannedQty(pickQty);
              handleFinishStatus(pickQty);
            }}
          />
        </SmartRow>
      </SmartSpace>
      <SmartBackButton>
        <SmartBlock>
          {scannedQty > 0 && (
            <SmartButton
              color="white"
              backgroundColor="red"
              loading={submitting}
              style={{ width: 'auto' }}
              onClick={() => {
                handleSubmit('skip', scannedQty);
              }}
            >
              {buttonTitle}
            </SmartButton>
          )}

          {/* {pickQty !== scannedQty && ( */}
          <SmartButton
            type="default"
            onClick={() => {
              onBack('scanSKU');
            }}
          >
            {t("smart.pick.chooseAnother")}
          </SmartButton>
          {/* )} */}
        </SmartBlock>
      </SmartBackButton>
    </>
  );
}
